import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Plus, X, Save, Mic, Square, Loader2, Paperclip, Lock, Trash2, FileText,
  Clock, Star, Target, Download, Sparkles, Check, MessageSquare,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select, Textarea, Field, Panel, CopyBtn } from "@/components/common";

const IMPORTANCE = ["critical", "high", "medium", "low", "unassessed"];
const POTENTIAL = ["unknown", "possible", "promising", "demonstrated"];
const SOURCE_TYPES = ["self_stated", "observed", "third_party", "documented", "user_interpretation", "ai_suggested"];
const FIELD_STATES = ["current", "unconfirmed", "unknown", "outdated", "disputed", "superseded"];
const DIMENSIONS = [
  ["reserved_outgoing", "Reserved ↔ Outgoing"], ["deliberate_quick", "Deliberate ↔ Quick to decide"],
  ["structured_flexible", "Structured ↔ Flexible"], ["detail_broad", "Detail-focused ↔ Broad-picture"],
  ["formal_informal", "Formal ↔ Informal"], ["direct_indirect", "Direct ↔ Indirect"],
  ["independent_collaborative", "Independent ↔ Collaborative"],
];
const CONFIDENCE = ["tentative", "moderate", "well_supported"];
const NOTE_CATEGORIES = ["meeting", "background", "interest_preference", "personality_observation", "life_event",
  "business_opportunity", "introduction", "commitment", "follow_up", "uncertainty", "general"];
const impColor = { critical: "disputed", high: "confirmed", medium: "uncertain", low: "candidate", unassessed: "candidate" };

export default function ContactProfile({ section, entityId, workspaceId, canEdit, isOwner, onReload }) {
  const [ent, setEnt] = useState(null);
  const [interests, setInterests] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get(`/entities/${entityId}/profile-full?workspace_id=${workspaceId}`);
    setEnt(data);
    api.get(`/strategic-interests?workspace_id=${workspaceId}`).then((r) => setInterests(r.data)).catch(() => {});
  }, [entityId, workspaceId]);
  useEffect(() => { load(); }, [load]);
  if (!ent) return <Loading />;
  const reload = () => { load(); onReload && onReload(); };
  const common = { ent, entityId, workspaceId, canEdit, isOwner, interests, reload };
  if (section === "overview") return <Overview {...common} />;
  if (section === "background") return <Background {...common} />;
  if (section === "interests") return <Interests {...common} />;
  if (section === "personality") return <Personality {...common} />;
  if (section === "notes") return <NotesTimeline {...common} />;
  if (section === "importance") return <Importance {...common} />;
  return null;
}

function SectionEditor({ title, ent, entityId, workspaceId, canEdit, sectionKey, fields, reload }) {
  const [form, setForm] = useState((ent.profile || {})[sectionKey] || {});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/entities/${entityId}/profile?workspace_id=${workspaceId}`, { section: sectionKey, data: form });
      toast.success("Saved"); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const sectionText = () => fields.filter((f) => (form[f.k] || "").trim())
    .map((f) => `${f.label}: ${form[f.k]}`).join("\n");
  return (
    <Panel className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="si-label">{title}</div>
        <div className="flex items-center gap-2">
          <CopyBtn text={sectionText()} label={`${title} copied`} title="Copy this section" testid={`copy-section-${sectionKey}`} />
          {canEdit && <Btn variant="primary" onClick={save} disabled={saving} data-testid={`profile-save-${sectionKey}`}><Save size={14} /> {saving ? "Saving…" : "Save"}</Btn>}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {fields.map((f) => (
          <Field key={f.k} label={f.label} copyValue={form[f.k] || ""}>
            {f.area
              ? <Textarea value={form[f.k] || ""} onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))} disabled={!canEdit} data-testid={`pf-${sectionKey}-${f.k}`} />
              : <Input value={form[f.k] || ""} onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))} disabled={!canEdit} data-testid={`pf-${sectionKey}-${f.k}`} />}
          </Field>
        ))}
      </div>
    </Panel>
  );
}

function Overview({ ent, entityId, workspaceId, canEdit, reload }) {
  const imp = ent.importance || {};
  const [summary, setSummary] = useState(null);
  const [sumBusy, setSumBusy] = useState("");
  const genSummary = async (kind) => {
    setSumBusy(kind);
    try { const { data } = await api.get(`/entities/${entityId}/summary?workspace_id=${workspaceId}&kind=${kind}&refresh=true`); setSummary({ kind, ...data }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not generate"); }
    finally { setSumBusy(""); }
  };
  return (
    <div className="space-y-5">
      <Panel className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="si-label">Current importance</span>
            <Badge value={impColor[imp.level] || "candidate"}>{imp.level || "unassessed"}</Badge>
            {imp.reason && <span className="text-sm" style={{ color: "var(--si-text-2)" }}>— {imp.reason}</span>}
            {ent.future_potential && <Badge value="candidate">potential: {ent.future_potential}</Badge>}
          </div>
          <div className="flex gap-2">
            <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={() => genSummary("prep")} disabled={sumBusy} data-testid="summary-prep-btn">{sumBusy === "prep" ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} Prepare for conversation</Btn>
            <Btn variant="ghost" className="!py-1 !px-3 text-xs" onClick={() => genSummary("full")} disabled={sumBusy} data-testid="summary-full-btn">{sumBusy === "full" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Full understanding</Btn>
          </div>
        </div>
        <div className="text-xs" style={{ color: "var(--si-text-2)" }}>Importance expresses relevance to your current objectives — not status or worth. Low or unassessed contacts are always retained.</div>
      </Panel>
      {summary && (
        <Panel className="p-5" data-testid="summary-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="si-label">{summary.kind === "prep" ? "Prepare for conversation" : "Full understanding"}</div>
            <button onClick={() => setSummary(null)}><X size={15} style={{ color: "var(--si-text-2)" }} /></button>
          </div>
          <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--si-text)" }}>{summary.text}</div>
          <div className="si-label mt-3">Every statement cites a saved fact [id]. {summary.generated_at ? `Generated ${new Date(summary.generated_at).toLocaleString()}.` : ""} Restricted facts are excluded for non-owners.</div>
        </Panel>
      )}
      <SectionEditor title="Overview" ent={ent} entityId={entityId} workspaceId={workspaceId} canEdit={canEdit} reload={reload} sectionKey="overview"
        fields={[
          { k: "preferred_name", label: "Preferred name" }, { k: "pronunciation", label: "Pronunciation" },
          { k: "role", label: "Current role" }, { k: "organization", label: "Organization" },
          { k: "location", label: "Location" }, { k: "met_context", label: "How / where / when we met" },
          { k: "last_interaction", label: "Most recent interaction" }, { k: "relationship_stage_note", label: "Relationship note" },
          { k: "what_to_remember", label: "What to remember about this person", area: true },
          { k: "open_questions", label: "Open questions & follow-ups", area: true },
        ]} />
    </div>
  );
}

function Background({ ent, entityId, workspaceId, canEdit, isOwner, reload }) {
  return (
    <div className="space-y-5">
      <SectionEditor title="Background & circumstances" ent={ent} entityId={entityId} workspaceId={workspaceId} canEdit={canEdit} reload={reload} sectionKey="background"
        fields={[
          { k: "education", label: "Education & qualifications", area: true }, { k: "professional_history", label: "Professional history", area: true },
          { k: "current_activities", label: "Current business activities / projects", area: true }, { k: "places", label: "Associated places" },
          { k: "family", label: "Family / household (voluntarily shared)", area: true }, { k: "life_events", label: "Significant life events & meaningful dates", area: true },
          { k: "community", label: "Community participation & affiliations", area: true }, { k: "goals", label: "Goals, ambitions, challenges, circumstances", area: true },
        ]} />
      <CustomFields ent={ent} entityId={entityId} workspaceId={workspaceId} canEdit={canEdit} isOwner={isOwner} reload={reload} />
    </div>
  );
}

function Interests({ ent, entityId, workspaceId, canEdit, reload }) {
  return (
    <SectionEditor title="Interests & preferences" ent={ent} entityId={entityId} workspaceId={workspaceId} canEdit={canEdit} reload={reload} sectionKey="interests"
      fields={[
        { k: "hobbies", label: "Hobbies, music, sports, food, travel", area: true }, { k: "topics", label: "Topics they enjoy discussing" },
        { k: "likes", label: "Stated preferences" }, { k: "dislikes", label: "Dislikes" },
        { k: "meeting_format", label: "Preferred meeting format" }, { k: "contact_channel", label: "Preferred contact channel" },
        { k: "contact_frequency", label: "Preferred contact frequency" }, { k: "availability", label: "Scheduling preferences / availability" },
        { k: "priorities", label: "Important personal/professional priorities", area: true }, { k: "accessibility", label: "Accessibility / accommodation preferences" },
      ]} />
  );
}

function CustomFields({ ent, entityId, workspaceId, canEdit, isOwner, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ group: "General", label: "", value: "", state: "current", source_type: "self_stated", attribution: "", observed_at: "", restricted: false });
  const fields = ent.custom_fields || [];
  const add = async () => {
    if (!f.label.trim() || !f.value.trim()) return toast.error("Label and value required");
    try { await api.post(`/entities/${entityId}/fields?workspace_id=${workspaceId}`, f); toast.success("Field added"); setOpen(false); setF({ ...f, label: "", value: "", observed_at: "" }); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/entities/${entityId}/fields/${id}?workspace_id=${workspaceId}`); reload(); };
  return (
    <Panel>
      <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
        <div className="si-label">Custom fields (progressive capture)</div>
        {canEdit && <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={() => setOpen((o) => !o)} data-testid="field-add-toggle"><Plus size={13} /> Add field</Btn>}
      </div>
      {open && (
        <div className="px-5 py-3 space-y-2" style={{ borderBottom: "1px solid var(--si-border)" }}>
          <div className="grid md:grid-cols-3 gap-2">
            <Field label="Group"><Input value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="field-group" /></Field>
            <Field label="Label"><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="field-label" /></Field>
            <Field label="Value"><Input value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="field-value" /></Field>
            <Field label="State"><Select value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}>{FIELD_STATES.map((x) => <option key={x} value={x}>{x}</option>)}</Select></Field>
            <Field label="Source"><Select value={f.source_type} onChange={(e) => setF({ ...f, source_type: e.target.value })} data-testid="field-source">{SOURCE_TYPES.map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}</Select></Field>
            <Field label="Attribution / date"><Input value={f.attribution} onChange={(e) => setF({ ...f, attribution: e.target.value })} placeholder="who said / where" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
            <input type="checkbox" checked={f.restricted} onChange={(e) => setF({ ...f, restricted: e.target.checked })} data-testid="field-restricted" /> <Lock size={12} /> Restricted (owner-only)
          </label>
          <Btn variant="primary" onClick={add} data-testid="field-save">Add field</Btn>
        </div>
      )}
      {fields.length === 0 ? <div className="text-sm px-5 py-5 text-center" style={{ color: "var(--si-text-2)" }}>No custom fields yet.</div> : (
        fields.map((x) => (
          <div key={x.id} className="flex items-start justify-between px-5 py-2.5" style={{ borderBottom: "1px solid var(--si-border)", opacity: x.state === "superseded" ? 0.55 : 1 }} data-testid={`field-${x.id}`}>
            <div>
              <div className="text-sm" style={{ color: "var(--si-text)" }}>
                <span className="si-label mr-2">{x.group}</span>{x.label}: <strong>{x.value}</strong>
                {x.restricted && <Lock size={11} className="inline ml-1.5" style={{ color: "var(--si-warning)" }} />}
              </div>
              <div className="si-label mt-0.5">{x.state} · {(x.source_type || "").replace(/_/g, " ")}{x.attribution ? ` · ${x.attribution}` : ""}</div>
            </div>
            {canEdit && <button onClick={() => del(x.id)} style={{ color: "var(--si-danger)" }} data-testid={`field-del-${x.id}`}><Trash2 size={14} /></button>}
          </div>
        ))
      )}
    </Panel>
  );
}

function Personality({ ent, entityId, workspaceId, canEdit, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ dimension: "", position: 50, description: "", confidence: "tentative", context: "", self_described: false, source: "", support_excerpt: "" });
  const obs = ent.personality || [];
  const add = async () => {
    if (!f.description.trim()) return toast.error("Description required");
    try { await api.post(`/entities/${entityId}/personality?workspace_id=${workspaceId}`, { ...f, dimension: f.dimension || null, observed_at: new Date().toISOString() }); toast.success("Observation added"); setOpen(false); setF({ ...f, description: "", support_excerpt: "" }); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/entities/${entityId}/personality/${id}?workspace_id=${workspaceId}`); reload(); };
  return (
    <div className="space-y-5">
      <Panel className="p-5">
        <div className="si-label mb-3">Dimensions (default: Unknown — never auto-scored)</div>
        <div className="space-y-2">
          {DIMENSIONS.map(([key, label]) => {
            const points = obs.filter((o) => o.dimension === key);
            return (
              <div key={key} className="flex items-center gap-3">
                <div className="text-xs w-56" style={{ color: "var(--si-text-2)" }}>{label}</div>
                <div className="flex-1 relative h-1.5 rounded" style={{ background: "var(--si-border)" }}>
                  {points.map((p) => p.position != null && (
                    <span key={p.id} className="absolute -top-1 w-3 h-3 rounded-full" title={p.description} style={{ left: `calc(${p.position}% - 6px)`, background: "var(--si-teal-light)" }} />
                  ))}
                </div>
                <div className="w-20 text-right">{points.length === 0 ? <span className="si-label">Unknown</span> : <Badge value="candidate">{points.length} obs</Badge>}</div>
              </div>
            );
          })}
        </div>
      </Panel>
      <Panel>
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
          <div className="si-label">Observations (contradictory observations may coexist)</div>
          {canEdit && <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={() => setOpen((o) => !o)} data-testid="personality-add-toggle"><Plus size={13} /> Add</Btn>}
        </div>
        {open && (
          <div className="px-5 py-3 space-y-2" style={{ borderBottom: "1px solid var(--si-border)" }}>
            <div className="grid md:grid-cols-2 gap-2">
              <Field label="Dimension (optional)"><Select value={f.dimension} onChange={(e) => setF({ ...f, dimension: e.target.value })} data-testid="personality-dimension"><option value="">— qualitative only —</option>{DIMENSIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
              <Field label={`Position (${f.position})`}><input type="range" min="0" max="100" value={f.position} onChange={(e) => setF({ ...f, position: parseInt(e.target.value) })} className="w-full" /></Field>
            </div>
            <Field label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="personality-desc" placeholder="e.g. Prefers concise, evidence-backed explanations" /></Field>
            <Field label="Supporting interaction / excerpt"><Input value={f.support_excerpt} onChange={(e) => setF({ ...f, support_excerpt: e.target.value })} /></Field>
            <div className="grid md:grid-cols-3 gap-2">
              <Field label="Confidence"><Select value={f.confidence} onChange={(e) => setF({ ...f, confidence: e.target.value })} data-testid="personality-confidence">{CONFIDENCE.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}</Select></Field>
              <Field label="Context"><Input value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} placeholder="e.g. at work" /></Field>
              <Field label="Source"><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}><input type="checkbox" checked={f.self_described} onChange={(e) => setF({ ...f, self_described: e.target.checked })} /> Self-described (not observer interpretation)</label>
            <Btn variant="primary" onClick={add} data-testid="personality-save">Add observation</Btn>
          </div>
        )}
        {obs.length === 0 ? <div className="text-sm px-5 py-5 text-center" style={{ color: "var(--si-text-2)" }}>No observations. Don't characterize someone from a single interaction.</div> : (
          obs.map((o) => (
            <div key={o.id} className="flex items-start justify-between px-5 py-2.5" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`personality-${o.id}`}>
              <div>
                <div className="text-sm" style={{ color: "var(--si-text)" }}>{o.description}</div>
                <div className="si-label mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {o.dimension && <Badge value="candidate">{o.dimension.replace(/_/g, " ")}</Badge>}
                  <Badge value={o.confidence === "well_supported" ? "confirmed" : "uncertain"}>{o.confidence.replace(/_/g, " ")}</Badge>
                  {o.self_described ? <span>self-described</span> : <span>observer interpretation</span>}
                  {o.context && <span>· {o.context}</span>}
                </div>
              </div>
              {canEdit && <button onClick={() => del(o.id)} style={{ color: "var(--si-danger)" }} data-testid={`personality-del-${o.id}`}><Trash2 size={14} /></button>}
            </div>
          ))
        )}
      </Panel>
    </div>
  );
}

function Importance({ ent, entityId, workspaceId, canEdit, interests, reload }) {
  const imp = ent.importance || {};
  const [f, setF] = useState({ level: imp.level || "unassessed", reason: imp.reason || "", review_date: imp.review_date || "" });
  const [ctx, setCtx] = useState({ strategic_interest_id: "", level: "medium", reason: "" });
  const [pot, setPot] = useState(ent.future_potential || "unknown");
  const save = async () => {
    try { await api.post(`/entities/${entityId}/importance?workspace_id=${workspaceId}`, f); await api.put(`/entities/${entityId}/dimensions?workspace_id=${workspaceId}`, { future_potential: pot }); toast.success("Importance updated"); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const saveCtx = async () => {
    if (!ctx.strategic_interest_id) return toast.error("Pick a strategic interest");
    try { await api.post(`/entities/${entityId}/importance?workspace_id=${workspaceId}`, ctx); toast.success("Contextual importance set"); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const [score, setScore] = useState(ent.priority_score || null);
  const compute = async (override) => {
    const { data } = await api.post(`/entities/${entityId}/priority-score?workspace_id=${workspaceId}`, override != null ? { manual_override: override } : {});
    setScore(data); reload();
  };
  const siName = (id) => (interests.find((x) => x.id === id) || {}).name || id;
  return (
    <div className="space-y-5">
      <Panel className="p-5">
        <div className="si-label mb-3 flex items-center gap-1.5"><Star size={13} /> Overall importance</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Level"><Select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} disabled={!canEdit} data-testid="importance-level">{IMPORTANCE.map((x) => <option key={x} value={x}>{x}</option>)}</Select></Field>
          <Field label="Future potential"><Select value={pot} onChange={(e) => setPot(e.target.value)} disabled={!canEdit} data-testid="importance-potential">{POTENTIAL.map((x) => <option key={x} value={x}>{x}</option>)}</Select></Field>
          <Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} disabled={!canEdit} data-testid="importance-reason" /></Field>
          <Field label="Review date (optional)"><Input type="date" value={f.review_date || ""} onChange={(e) => setF({ ...f, review_date: e.target.value })} disabled={!canEdit} /></Field>
        </div>
        {canEdit && <Btn variant="primary" className="mt-3" onClick={save} data-testid="importance-save"><Save size={14} /> Save importance</Btn>}
        <div className="text-xs mt-3" style={{ color: "var(--si-text-2)" }}>Assigned by {imp.assigned_by || "—"}{imp.assessed_at ? ` · ${new Date(imp.assessed_at).toLocaleDateString()}` : ""}. Low importance never triggers deletion or exclusion.</div>
      </Panel>

      <Panel className="p-5">
        <div className="si-label mb-3 flex items-center gap-1.5"><Target size={13} /> Context-specific importance (per strategic interest)</div>
        {(ent.importance_context || []).length > 0 && (
          <div className="mb-3 space-y-1">
            {ent.importance_context.map((c) => (
              <div key={c.strategic_interest_id} className="flex items-center gap-2 text-sm" data-testid={`ctx-imp-${c.strategic_interest_id}`}>
                <Badge value={impColor[c.level]}>{c.level}</Badge> <span style={{ color: "var(--si-text)" }}>{siName(c.strategic_interest_id)}</span>
                {c.reason && <span style={{ color: "var(--si-text-2)" }}>— {c.reason}</span>}
              </div>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="grid md:grid-cols-3 gap-2">
            <Field label="Strategic interest"><Select value={ctx.strategic_interest_id} onChange={(e) => setCtx({ ...ctx, strategic_interest_id: e.target.value })} data-testid="ctx-interest"><option value="">— select —</option>{interests.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</Select></Field>
            <Field label="Level"><Select value={ctx.level} onChange={(e) => setCtx({ ...ctx, level: e.target.value })}>{IMPORTANCE.map((x) => <option key={x} value={x}>{x}</option>)}</Select></Field>
            <Field label="Reason"><Input value={ctx.reason} onChange={(e) => setCtx({ ...ctx, reason: e.target.value })} /></Field>
          </div>
        )}
        {canEdit && <Btn variant="secondary" className="mt-2" onClick={saveCtx} data-testid="ctx-save">Set contextual importance</Btn>}
        <div className="text-xs mt-2" style={{ color: "var(--si-text-2)" }}>Context ratings sit alongside the overall rating and never overwrite it.</div>
      </Panel>

      <Panel className="p-5">
        <div className="si-label mb-3">Optional priority score (categories remain the default)</div>
        {score ? (
          <div>
            <div className="text-3xl font-mono" style={{ color: "var(--si-teal-light)" }} data-testid="priority-score-value">{score.value}<span className="text-sm" style={{ color: "var(--si-text-2)" }}>/100</span></div>
            <div className="text-xs mt-2 space-y-0.5" style={{ color: "var(--si-text-2)" }}>
              {Object.entries(score.factors || {}).map(([k, v]) => <div key={k}>{k.replace(/_/g, " ")}: {String(v.value)} → {v.points} pts × {v.weight}</div>)}
            </div>
            {canEdit && <div className="flex items-center gap-2 mt-3"><Input type="number" min="0" max="100" placeholder="manual override" style={{ width: 140 }} onKeyDown={(e) => e.key === "Enter" && compute(parseInt(e.target.value))} data-testid="score-override" /><Btn variant="ghost" onClick={() => compute()} data-testid="score-recompute">Recompute</Btn></div>}
            <div className="text-xs mt-2" style={{ color: "var(--si-text-2)" }}>{score.note}</div>
          </div>
        ) : canEdit ? <Btn variant="secondary" onClick={() => compute()} data-testid="score-compute">Compute priority score</Btn> : <span className="si-label">Not computed.</span>}
      </Panel>
    </div>
  );
}

function NotesTimeline({ ent, entityId, workspaceId, canEdit, isOwner, reload }) {
  const [notes, setNotes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [tab, setTab] = useState("notes");
  const [open, setOpen] = useState(false);
  const [extractNote, setExtractNote] = useState(null);
  const loadNotes = useCallback(async () => {
    const [n, t] = await Promise.all([
      api.get(`/notes?workspace_id=${workspaceId}&entity_id=${entityId}`),
      api.get(`/entities/${entityId}/timeline?workspace_id=${workspaceId}`),
    ]);
    setNotes(n.data); setTimeline(t.data.events || []);
  }, [entityId, workspaceId]);
  useEffect(() => { loadNotes(); }, [loadNotes]);
  const del = async (id) => { await api.delete(`/notes/${id}?workspace_id=${workspaceId}`); loadNotes(); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Btn variant={tab === "notes" ? "secondary" : "ghost"} className="!py-1 !px-3 text-xs" onClick={() => setTab("notes")} data-testid="nt-tab-notes"><FileText size={13} /> Notes</Btn>
          <Btn variant={tab === "timeline" ? "secondary" : "ghost"} className="!py-1 !px-3 text-xs" onClick={() => setTab("timeline")} data-testid="nt-tab-timeline"><Clock size={13} /> Timeline</Btn>
        </div>
        {canEdit && <Btn variant="primary" className="!py-1 !px-3 text-xs" onClick={() => setOpen(true)} data-testid="note-add-btn"><Plus size={13} /> Quick add note</Btn>}
      </div>

      {tab === "notes" ? (
        notes.length === 0 ? <EmptyState icon={FileText} title="No notes yet" hint="Capture meetings, background, preferences, commitments." /> : (
          <div className="space-y-2">
            {notes.map((n) => (
              <Panel key={n.id} className="p-4" style={{}}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge value="candidate">{n.custom_category || n.category}</Badge>
                      {n.restricted && <Badge value="disputed"><Lock size={10} /> owner-only</Badge>}
                      <span className="si-label">{n.event_date ? new Date(n.event_date).toLocaleDateString() : new Date(n.created_at).toLocaleDateString()}</span>
                      {(n.tags || []).map((t) => <span key={t} className="si-label">#{t}</span>)}
                    </div>
                    {n.title && <div className="text-sm font-medium" style={{ color: "var(--si-text)" }}>{n.title}</div>}
                    <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--si-text-2)" }}>{n.body}</div>
                    {(n.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {n.attachments.map((a) => <AttachmentChip key={a.id} att={a} workspaceId={workspaceId} />)}
                      </div>
                    )}
                    <div className="si-label mt-1.5">by {n.created_by}{(n.edit_history || []).length > 0 ? ` · edited ${n.edit_history.length}×` : ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {canEdit && <button onClick={() => setExtractNote(n)} title="Suggest profile updates & tasks from this note" style={{ color: "var(--si-teal-light)" }} data-testid={`note-extract-${n.id}`}><Sparkles size={14} /></button>}
                    {canEdit && <AttachBtn noteId={n.id} workspaceId={workspaceId} onDone={loadNotes} />}
                    {canEdit && <button onClick={() => del(n.id)} style={{ color: "var(--si-danger)" }} data-testid={`note-del-${n.id}`}><Trash2 size={14} /></button>}
                  </div>
                </div>
              </Panel>
            ))}
          </div>
        )
      ) : (
        timeline.length === 0 ? <EmptyState icon={Clock} title="No timeline events yet" /> : (
          <div style={{ borderLeft: "2px solid var(--si-border)", paddingLeft: 16 }}>
            {timeline.map((e, i) => (
              <div key={i} className="relative pb-4" data-testid={`timeline-${e.type}-${i}`}>
                <span className="absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full" style={{ background: "var(--si-teal)" }} />
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge value="candidate">{e.type}</Badge>
                  <span className="si-label">{e.date ? new Date(e.date).toLocaleDateString() : "—"}</span>
                  {e.restricted && <Lock size={11} style={{ color: "var(--si-warning)" }} />}
                  {e.source_type && <span className="si-label">{e.source_type.replace(/_/g, " ")}</span>}
                </div>
                <div className="text-sm" style={{ color: "var(--si-text)" }}>{e.title}</div>
              </div>
            ))}
          </div>
        )
      )}

      {open && <NoteModal entityId={entityId} workspaceId={workspaceId} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); loadNotes(); reload(); }} />}
      {extractNote && <ExtractReview note={extractNote} entityId={entityId} workspaceId={workspaceId} onClose={() => setExtractNote(null)} onCommitted={() => { setExtractNote(null); loadNotes(); reload(); }} />}
    </div>
  );
}

function ExtractReview({ note, entityId, workspaceId, onClose, onCommitted }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [sel, setSel] = useState({ fields: {}, personality: {}, connections: {}, tasks: {} });
  const idem = useRef(Math.random().toString(36).slice(2) + Date.now());
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/profile/extract?workspace_id=${workspaceId}`, { entity_id: entityId, text: note.body, note_id: note.id });
        setData(data);
        const s = { fields: {}, personality: {}, connections: {}, tasks: {} };
        data.fields.forEach((f) => (s.fields[f.ref] = { on: !f.conflict, value: f.value, group: f.group, label: f.label, source_type: f.source_type }));
        data.personality.forEach((p) => (s.personality[p.ref] = { on: false, description: p.description, dimension: p.dimension, confidence: p.confidence, self_described: p.self_described, excerpt: p.excerpt }));
        data.connections.forEach((c) => (s.connections[c.ref] = { on: false, name: c.name, relationship_type: c.relationship_type || "knows", action: (c.duplicates || []).length ? "" : "create", link_id: "" }));
        data.commitments.forEach((c) => (s.tasks[c.ref] = { on: !c.needs_clarification, action: c.action, due_date: c.due_date || "", committer: c.who === "contact" ? "contact" : "me", recipient: c.recipient || "", excerpt: c.excerpt, needs: c.needs_clarification }));
        setSel(s);
      } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Extraction failed"); onClose(); }
      finally { setBusy(false); }
    })();
    // eslint-disable-next-line
  }, []);
  const upd = (k, ref, patch) => setSel((p) => ({ ...p, [k]: { ...p[k], [ref]: { ...p[k][ref], ...patch } } }));
  const commit = async () => {
    const fields = Object.values(sel.fields).filter((x) => x.on).map((x) => ({ group: x.group, label: x.label, value: x.value, source_type: x.source_type }));
    const personality = Object.values(sel.personality).filter((x) => x.on).map((x) => ({ dimension: x.dimension, description: x.description, confidence: x.confidence, self_described: x.self_described, excerpt: x.excerpt }));
    const conns = Object.values(sel.connections).filter((x) => x.on);
    const unresolved = conns.filter((c) => (data.connections.find((d) => d.name === c.name)?.duplicates || []).length && !(c.action === "create" || (c.action === "link" && c.link_id)));
    if (unresolved.length) return toast.error(`Choose link-or-create for: ${unresolved.map((c) => c.name).join(", ")}`);
    const tasks = Object.values(sel.tasks).filter((x) => x.on).map((x) => ({ action: x.action, due_date: x.due_date || null, committer: x.committer, recipient: x.recipient, excerpt: x.excerpt }));
    const needDate = Object.values(sel.tasks).filter((x) => x.on && !x.due_date && x.needs);
    if (needDate.length) return toast.error("Set a date (or clear the flag) for ambiguous commitments before saving");
    setBusy(true);
    try {
      const { data: r } = await api.post(`/profile/extract/commit?workspace_id=${workspaceId}`, {
        idempotency_key: idem.current, entity_id: entityId, note_id: note.id,
        fields, personality, connections: conns.map((c) => ({ name: c.name, relationship_type: c.relationship_type, action: c.action || "create", link_id: c.link_id })), tasks,
      });
      toast.success(`Applied: ${r.fields} fields, ${r.personality} personality, ${r.connections} links, ${r.tasks} tasks`);
      onCommitted();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Save failed"); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 4 }} onClick={(e) => e.stopPropagation()} data-testid="extract-modal">
        <div className="flex items-center justify-between mb-3"><div className="si-label flex items-center gap-1.5"><Sparkles size={13} /> Review suggested updates</div><button onClick={onClose}><X size={18} style={{ color: "var(--si-text-2)" }} /></button></div>
        <div className="text-xs mb-3" style={{ color: "var(--si-text-2)" }}>Nothing is saved until you accept. AI suggestions are not verification — original note wording is preserved.</div>
        {busy && !data ? <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto" style={{ color: "var(--si-teal-light)" }} /></div> : data && (
          <div className="space-y-4">
            {data.commitments.length > 0 && (
              <div>
                <div className="si-label mb-1">Follow-up tasks from commitments</div>
                {data.commitments.map((c) => { const st = sel.tasks[c.ref]; return (
                  <div key={c.ref} className="p-2 mb-2 text-sm" style={{ border: `1px solid ${c.needs_clarification ? "var(--si-warning)" : "var(--si-border)"}`, borderRadius: 2 }} data-testid={`extract-task-${c.ref}`}>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={st.on} onChange={(e) => upd("tasks", c.ref, { on: e.target.checked })} data-testid={`extract-task-on-${c.ref}`} /> <span style={{ color: "var(--si-text)" }}>{c.action}</span></label>
                    <div className="italic si-label mt-1">“{c.excerpt}”</div>
                    <div className="grid grid-cols-3 gap-2 mt-1.5">
                      <Select value={st.committer} onChange={(e) => upd("tasks", c.ref, { committer: e.target.value })} data-testid={`extract-task-who-${c.ref}`}><option value="me">I promised</option><option value="contact">{data.entity_name} promised</option></Select>
                      <Input type="date" value={st.due_date} onChange={(e) => upd("tasks", c.ref, { due_date: e.target.value })} data-testid={`extract-task-date-${c.ref}`} />
                      <Input value={st.recipient} onChange={(e) => upd("tasks", c.ref, { recipient: e.target.value })} placeholder="recipient" />
                    </div>
                    {c.needs_clarification && <div className="si-label mt-1" style={{ color: "var(--si-warning)" }}>Needs clarification (who / when / responsibility) before saving.</div>}
                  </div>
                ); })}
              </div>
            )}
            {data.connections.length > 0 && (
              <div>
                <div className="si-label mb-1">Connections</div>
                {data.connections.map((c) => { const st = sel.connections[c.ref]; return (
                  <div key={c.ref} className="p-2 mb-2 text-sm" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid={`extract-conn-${c.ref}`}>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={st.on} onChange={(e) => upd("connections", c.ref, { on: e.target.checked })} data-testid={`extract-conn-on-${c.ref}`} /> <span style={{ color: "var(--si-text)" }}>{c.name} — {(c.relationship_type || "knows").replace(/_/g, " ")}</span></label>
                    <div className="italic si-label mt-1">“{c.excerpt}”</div>
                    {(c.duplicates || []).length > 0 ? (
                      <div className="mt-1" style={{ borderLeft: `2px solid ${st.action ? "var(--si-teal)" : "var(--si-warning)"}`, paddingLeft: 8 }}>
                        {!st.action && <div className="si-label" style={{ color: "var(--si-warning)" }}>Ambiguous — choose one:</div>}
                        <label className="flex items-center gap-1.5"><input type="radio" checked={st.action === "create"} onChange={() => upd("connections", c.ref, { action: "create", link_id: "" })} /> Create new</label>
                        {c.duplicates.map((d) => <label key={d.id} className="flex items-center gap-1.5"><input type="radio" checked={st.action === "link" && st.link_id === d.id} onChange={() => upd("connections", c.ref, { action: "link", link_id: d.id })} data-testid={`extract-conn-link-${c.ref}`} /> Link to {d.name} <span className="si-label">{d.type}</span></label>)}
                      </div>
                    ) : <div className="si-label mt-1">No match — will be created new.</div>}
                  </div>
                ); })}
              </div>
            )}
            {data.fields.length > 0 && (
              <div>
                <div className="si-label mb-1">Profile details</div>
                {data.fields.map((f) => { const st = sel.fields[f.ref]; return (
                  <div key={f.ref} className="p-2 mb-2 text-sm" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid={`extract-field-${f.ref}`}>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={st.on} onChange={(e) => upd("fields", f.ref, { on: e.target.checked })} data-testid={`extract-field-on-${f.ref}`} /> <span style={{ color: "var(--si-text)" }}>{f.group} · {f.label}</span></label>
                    <Input className="mt-1" value={st.value} onChange={(e) => upd("fields", f.ref, { value: e.target.value })} />
                    <div className="italic si-label mt-1">“{f.excerpt}” · {(f.source_type || "").replace(/_/g, " ")}{f.conflict ? ` · conflicts with existing "${f.existing_value}" (kept as history)` : ""}</div>
                  </div>
                ); })}
              </div>
            )}
            {data.personality.length > 0 && (
              <div>
                <div className="si-label mb-1">Personality observations</div>
                {data.personality.map((p) => { const st = sel.personality[p.ref]; return (
                  <label key={p.ref} className="flex items-start gap-2 p-2 mb-2 text-sm" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid={`extract-pers-${p.ref}`}>
                    <input type="checkbox" checked={st.on} onChange={(e) => upd("personality", p.ref, { on: e.target.checked })} />
                    <span><span style={{ color: "var(--si-text)" }}>{p.description}</span> <span className="si-label">[{p.confidence}{p.self_described ? ", self-described" : ""}]</span><div className="italic si-label">“{p.excerpt}”</div></span>
                  </label>
                ); })}
              </div>
            )}
            {!data.commitments.length && !data.connections.length && !data.fields.length && !data.personality.length && <EmptyState icon={Sparkles} title="No structured updates found" hint="The note is kept as-is." />}
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
              <Btn variant="primary" onClick={commit} disabled={busy} data-testid="extract-commit"><Check size={14} /> Apply selected</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentChip({ att, workspaceId }) {
  const [url, setUrl] = useState(null);
  const view = async () => {
    try {
      const resp = await api.get(`/files/${att.id}/download?workspace_id=${workspaceId}`, { responseType: "blob" });
      const u = URL.createObjectURL(resp.data); setUrl(u); window.open(u, "_blank");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Cannot open file"); }
  };
  return <button onClick={view} className="text-xs flex items-center gap-1 px-2 py-1" style={{ border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-silver)" }} data-testid={`attach-${att.id}`}>{att.restricted && <Lock size={10} />}<Paperclip size={11} /> {att.original_filename}</button>;
}

function AttachBtn({ noteId, workspaceId, onDone }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/notes/${noteId}/attachments?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Attached"); onDone(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  return (<><input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp" className="hidden" onChange={up} data-testid={`note-attach-input-${noteId}`} /><button onClick={() => ref.current.click()} style={{ color: "var(--si-text-2)" }} data-testid={`note-attach-${noteId}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}</button></>);
}

function NoteModal({ entityId, workspaceId, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", body: "", category: "meeting", custom_category: "", tags: "", event_date: "", source_type: "user_interpretation", restricted: false });
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const mr = useRef(null); const chunks = useRef([]); const stream = useRef(null);
  const startRec = async () => {
    try {
      const st = await navigator.mediaDevices.getUserMedia({ audio: true }); stream.current = st;
      let mime = ""; for (const mt of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) { if (MediaRecorder.isTypeSupported(mt)) { mime = mt; break; } }
      const m = mime ? new MediaRecorder(st, { mimeType: mime }) : new MediaRecorder(st); mr.current = m; chunks.current = [];
      m.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      m.onstop = async () => {
        const blob = new Blob(chunks.current, { type: m.mimeType || "audio/webm" });
        setBusy(true);
        try {
          const fd = new FormData(); fd.append("file", blob, "note.webm");
          const { data } = await api.post(`/voice/transcribe?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setF((p) => ({ ...p, body: (p.body ? p.body + "\n" : "") + (data.transcript || "") }));
          toast.success("Transcribed");
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Transcription failed"); }
        finally { setBusy(false); }
      };
      m.start(); setRec(true);
    } catch { toast.error("Microphone unavailable"); }
  };
  const stopRec = () => { try { mr.current?.stop(); } catch {} stream.current?.getTracks().forEach((t) => t.stop()); setRec(false); };
  const save = async () => {
    if (!f.body.trim() && !f.title.trim()) return toast.error("Note is empty");
    setBusy(true);
    try {
      await api.post(`/notes?workspace_id=${workspaceId}`, {
        ...f, entity_ids: [entityId], tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        event_date: f.event_date || null,
      });
      toast.success("Note saved"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 4 }} onClick={(e) => e.stopPropagation()} data-testid="note-modal">
        <div className="flex items-center justify-between mb-4"><div className="si-label">Quick add note</div><button onClick={onClose}><X size={18} style={{ color: "var(--si-text-2)" }} /></button></div>
        <div className="space-y-3">
          <Field label="Title (optional)"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="note-title" /></Field>
          <Field label="Note">
            <Textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="note-body" placeholder="Type, or dictate with the mic…" />
          </Field>
          <div className="flex items-center gap-2">
            {!rec ? <Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={startRec} disabled={busy} data-testid="note-voice-start"><Mic size={13} /> Dictate</Btn>
                  : <Btn variant="danger" className="!py-1 !px-2 text-xs" onClick={stopRec} data-testid="note-voice-stop"><Square size={13} /> Stop & transcribe</Btn>}
            {busy && <Loader2 size={14} className="animate-spin" style={{ color: "var(--si-teal-light)" }} />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><Select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="note-category">{NOTE_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}</Select></Field>
            <Field label="Event date"><Input type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} data-testid="note-event-date" /></Field>
          </div>
          <Field label="Tags (comma separated)"><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="note-tags" /></Field>
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}><input type="checkbox" checked={f.restricted} onChange={(e) => setF({ ...f, restricted: e.target.checked })} data-testid="note-restricted" /> <Lock size={12} /> Restricted (owner-only)</label>
          <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="note-save">{busy ? "Saving…" : "Save note"}</Btn>
        </div>
      </div>
    </div>
  );
}
